if (!customElements.get("my-pinning-list")) {
  class MyPinningList extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isSnapping = false;
      this._snapTimer = null;
      this.startY = 0;
      this.touchDeltaY = 0;
      this.touchMoved = false;
    }

    connectedCallback() {
      this.list = this.querySelector(".pcp__list");
      this.items = this.querySelectorAll(".pcp__item");
      this.wrapper = this.closest(".my-pcp-wrapper");
      this.pcp = this.wrapper.querySelector(".pcp"); // 粘滞区域
      this.heading = this.wrapper.querySelector(".page-width");

      this.computeSizes();

      // ====== PC 端滚轮
      this.onWheel = this.handleWheel.bind(this);
      this.wrapper.addEventListener("wheel", this.onWheel, { passive: false });

      // ====== 移动端 touch
      this.onTouchStart = this.handleTouchStart.bind(this);
      this.onTouchMove = this.handleTouchMove.bind(this);
      this.onTouchEnd = this.handleTouchEnd.bind(this);

      this.wrapper.addEventListener("touchstart", this.onTouchStart, {
        passive: false,
      });
      this.wrapper.addEventListener("touchmove", this.onTouchMove, {
        passive: false,
      });
      this.wrapper.addEventListener("touchend", this.onTouchEnd, {
        passive: false,
      });

      // snap 动画结束 → 解锁
      this.onTransitionEnd = () => {
        this.isSnapping = false;
      };
      this.list.addEventListener("transitionend", this.onTransitionEnd);

      this.setupWindowResize();
      this.initScroll(); // 默认 Motion.scroll
    }

    disconnectedCallback() {
      this.destroyScrollEvent();
      window.removeEventListener("resize", this.debouncedOnWindowResize);
      this.wrapper.removeEventListener("wheel", this.onWheel);

      this.wrapper.removeEventListener("touchstart", this.onTouchStart);
      this.wrapper.removeEventListener("touchmove", this.onTouchMove);
      this.wrapper.removeEventListener("touchend", this.onTouchEnd);

      this.list.removeEventListener("transitionend", this.onTransitionEnd);
    }

    // ====== 平滑滚动（非分页模式）
    initScroll() {
      const maxTranslateX = Math.max(0, this.totalWidth - this.itemWidth);
      this.cancel = Motion.scroll(
        (progress) => {
          if (!this.isPinnedVisible()) {
            const translateValue = progress * maxTranslateX;
            this.list.style.removeProperty("transition");
            this.list.style.setProperty(
              "--translateValue",
              `${translateValue}px`
            );
          }
        },
        { target: this.wrapper, offset: ["start start", "end end"] }
      );
    }

    destroyScrollEvent() {
      this.cancel && this.cancel();
      this.cancel = null;
    }

    // ====== 判断粘滞区是否完全可见
    isPinnedVisible() {
      const rect = this.wrapper.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;

      // ✅ wrapper 顶部到了屏幕顶部，且底部还在屏幕下方（进入粘滞区）
      const hitTop = rect.top <= 0 && rect.bottom > vh;

      // ✅ wrapper 底部还在屏幕范围内，且顶部已经在屏幕上方（粘滞区内）
      const hitBottom = rect.top < 0 && rect.bottom >= vh;

      return hitTop || hitBottom;
    }

    // ====== PC 端滚轮分页
    handleWheel(e) {
      if (!this.isPinnedVisible()) return;

      const atFirst = this.currentIndex === 0;
      const atLast = this.currentIndex === this.lastIndex;

      // ✅ 到达边界时，允许默认滚动离开粘滞区
      if ((e.deltaY < 0 && atFirst) || (e.deltaY > 0 && atLast)) {
        return;
      }

      e.preventDefault();
      if (this.isSnapping) return;

      if (this.cancel) this.destroyScrollEvent();

      this.isSnapping = true;
      if (e.deltaY > 0 && !atLast) this.currentIndex++;
      if (e.deltaY < 0 && !atFirst) this.currentIndex--;

      this.snapToIndex(this.currentIndex);
    }

    // ====== 移动端 touch 分页
    handleTouchStart(e) {
      if (!this.isPinnedVisible()) return;
      this.startY = e.touches[0].clientY;
      this.touchMoved = false;
    }

    handleTouchMove(e) {
      if (!this.isPinnedVisible()) return;

      const currentY = e.touches[0].clientY;
      this.touchDeltaY = currentY - this.startY;

      const atFirst = this.currentIndex === 0;
      const atLast = this.currentIndex === this.lastIndex;

      // ✅ 只有在中间页 或 允许翻页方向时才阻止默认
      if (
        (this.touchDeltaY < 0 && !atLast) ||
        (this.touchDeltaY > 0 && !atFirst)
      ) {
        if (e.cancelable) {
          e.preventDefault();
        }
      }

      this.touchMoved = true;
    }

    handleTouchEnd() {
      if (!this.isPinnedVisible() || !this.touchMoved) return;

      const threshold = 50; // 滑动阈值
      const atFirst = this.currentIndex === 0;
      const atLast = this.currentIndex === this.lastIndex;

      if (this.cancel) this.destroyScrollEvent();

      if (this.touchDeltaY < -threshold && !atLast) {
        // 向上滑动（内容向上，显示下一张）
        this.isSnapping = true;
        this.currentIndex++;
        this.snapToIndex(this.currentIndex);
      } else if (this.touchDeltaY > threshold && !atFirst) {
        // 向下滑动（内容向下，显示上一张）
        this.isSnapping = true;
        this.currentIndex--;
        this.snapToIndex(this.currentIndex);
      }
      // ✅ 边界方向或滑动距离不足 → 不做处理，交给默认滚动

      this.touchDeltaY = 0;
      this.touchMoved = false;
    }

    // ====== 对齐到指定卡片
    snapToIndex(index) {
      const offset = index * (this.itemWidth + this.gap);
      this.list.style.transition =
        "transform 380ms cubic-bezier(.25,.46,.45,.94)";
      this.list.style.transform = `translate3d(-${offset}px,0,0)`;

      // ✅ 边界滚动：使用 requestAnimationFrame 确保流畅性
      requestAnimationFrame(() => {
        const wrapperRect = this.wrapper.getBoundingClientRect();

        if (this.currentIndex === this.lastIndex) {
          // 切换到最后一张 → 滚动到 wrapper 底部
          const scrollToBottom =
            window.scrollY + wrapperRect.bottom - window.innerHeight;
          window.scrollTo({
            top: scrollToBottom,
            behavior: "smooth",
          });
        } else if (this.currentIndex === 0) {
          // 切换到第一张 → 滚动到 wrapper 顶部
          const scrollToTop = window.scrollY + wrapperRect.top;
          window.scrollTo({
            top: scrollToTop,
            behavior: "smooth",
          });
        }
      });

      clearTimeout(this._snapTimer);
      this._snapTimer = setTimeout(() => {
        this.isSnapping = false;
      }, 400);
    }

    // ====== 尺寸计算
    computeSizes() {
      const cs = getComputedStyle(this.list);
      this.gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;
      this.itemWidth = this.items[0]?.offsetWidth || 0;
      this.lastIndex = Math.max(0, this.items.length - 1);
      this.totalWidth =
        this.items.length * (this.itemWidth + this.gap) - this.gap;
    }

    setupWindowResize() {
      this.debouncedOnWindowResize = debounce(() => this.onWindowResize(), 300);
      window.addEventListener("resize", this.debouncedOnWindowResize, false);
    }

    onWindowResize() {
      const oldIndex = this.currentIndex;
      this.computeSizes();
      this.currentIndex = Math.min(oldIndex, this.lastIndex);

      if (!this.isPinnedVisible()) {
        this.destroyScrollEvent();
        this.initScroll();
      } else {
        this.snapToIndex(this.currentIndex);
      }
    }
  }

  customElements.define("my-pinning-list", MyPinningList);
}
